// A single Conversation document accumulates every call/exchange a phone number
// (or web GUID) ever has — see backend/src/models/Conversation.js. To present
// these as the "separate calls" they really are, we segment the flat message
// list into sessions: voice messages carry metadata.callSid (authoritative —
// each Twilio call is one SID), and SMS/web (no SID) fall back to a time-gap
// heuristic.
export const SESSION_GAP_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Segment a conversation's flat message array into distinct calls/sessions.
 * Returns an array of { callSid, startTs, lastTs, messages } in chronological
 * order. Safe on undefined/empty input (returns []).
 */
export const groupMessagesIntoSessions = (messages = []) => {
    // Copy before sorting so we never mutate the caller's array; persisted order
    // should already be chronological, but don't rely on it.
    const sorted = [...messages].sort(
        (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
    );

    const sessions = [];
    for (const msg of sorted) {
        const callSid = msg.metadata?.callSid || null;
        const ts = new Date(msg.timestamp).getTime();
        const current = sessions[sessions.length - 1];

        let startNew = !current;
        if (current) {
            if (callSid && current.callSid) {
                // Both have a call SID — a change means a distinct call, even
                // if it happened moments after the previous one.
                startNew = callSid !== current.callSid;
            } else if (callSid || current.callSid) {
                // One side has a SID and the other doesn't — different channels
                // sharing one identifier shouldn't happen (unique per channel),
                // but treat the transition as a boundary to be safe.
                startNew = true;
            } else {
                // No SID on either (SMS/web) — split on a long silence.
                startNew = ts - current.lastTs > SESSION_GAP_MS;
            }
        }

        if (startNew) {
            sessions.push({ callSid, startTs: ts, lastTs: ts, messages: [msg] });
        } else {
            current.messages.push(msg);
            current.lastTs = ts;
        }
    }

    return sessions;
};

/** Human label for a session, given the conversation channel. */
export const sessionNoun = (identifierType) =>
    identifierType === 'voice' ? 'call' : 'session';
